import React, { useState, useEffect } from 'react';
import { KTIcon, toAbsoluteUrl } from '../../../helpers';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import DatePicker from 'react-datepicker';
import { get_all_coming_holidays } from '../api/widgetApi';
import { formatDatePickr, formatDateWithoutTime } from '../../../../app/helper-functions/DateFunction';
import { Holiday } from '../../../../app/modules/accounts/components/settings/SettingsModel';

type Props = {
  className: string;
};

const HolidayWidget: React.FC<Props> = ({ className }) => {
  const [listingData, setListingData] = useState<Holiday[]>([]);
  const currentStartDate = new Date();
  const currentYear = currentStartDate.getFullYear();
  const lastDayOfCurrentMonth = new Date(currentStartDate.getFullYear(), currentStartDate.getMonth() + 1, 0);

  const [startDate, setStartDate] = useState<string | null>(formatDatePickr(currentStartDate));
  const [endDate, setEndDate] = useState<string | null>(formatDatePickr(lastDayOfCurrentMonth));

  const { data: filterData, refetch: filteredHolidays } = useQuery(
    'filterDataHolidays',
    () => get_all_coming_holidays(startDate, endDate),
    {
      onSuccess: (filteredData) => {
        setListingData(filteredData?.data || []);
      },
      onError: (error: any) => {
        if (error.response) {
          toast.error('Failed to fetch holidays.');
        }
      },
      enabled: true,
      refetchOnWindowFocus: true,
    }
  );

  const handleApplyFilters = () => {
    filteredHolidays();
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setListingData([]);
  };

  useEffect(() => {
    if (filterData?.data) {
      setListingData(filterData.data || []);
    }
  }, [filterData]);

  return (
    <div className={clsx('card', className)}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold text-dark'>Upcoming Holidays</span>
        </h3>
        <div className='card-toolbar'>
          <div className='d-flex'>
            <DatePicker
              selected={startDate ? new Date(startDate) : null}
              onChange={(date) => setStartDate(date ? formatDatePickr(date) : null)}
              placeholderText='From Date'
              className='form-control'
              dateFormat='dd-MM-yyyy'
              isClearable
              minDate={new Date()}
            />
            <DatePicker
              selected={endDate ? new Date(endDate) : null}
              onChange={(date) => setEndDate(date ? formatDatePickr(date) : null)}
              placeholderText='To Date'
              className='form-control ms-2'
              dateFormat='dd-MM-yyyy'
              isClearable
              minDate={startDate ? new Date(startDate) : new Date()}
            />
            <button className='btn btn-primary ms-3' onClick={handleApplyFilters} title="Apply Filter">
              <i className="fa fa-filter"></i>
            </button>
            <button className='btn btn-secondary ms-2' onClick={handleClearFilters} title="Clear Filter">
              Clear
            </button>
          </div>
        </div>
      </div>
      <div className='card-body pt-5'>
        {listingData?.length > 0 ? (
          <div>
            {listingData.map((listData) => (
              <div key={listData?.id} className='d-flex align-items-sm-center mb-7'>
                <div className='symbol symbol-50px me-5'>
                  <span className='symbol-label'>
                    <img
                      src={toAbsoluteUrl('/media/svg/misc/hot-air-balloon.svg')}
                      className='h-50 align-self-center'
                      alt=''
                    />
                  </span>
                </div>
                <div className='d-flex align-items-center flex-row-fluid flex-wrap'>
                  <div className='flex-grow-1 me-2'>
                    <a className='text-gray-800 text-hover-primary fs-6 fw-bold'>
                      {listData?.title || 'No Title'}
                    </a>
                  </div>
                  <div className='flex-grow-1 me-2'>
                    <span className='text-muted fs-7'>
                      {"A wonderful day to relax, reflect, and enjoy precious moments with your family."}
                    </span>
                  </div>
                  <div className='flex-grow-1 me-2'>
                    <span className='text-muted fs-7'>
                      {listData?.holiday_type_name || 'Unknown Type'}
                    </span>
                  </div>
                  <span className='badge badge-light fw-bold my-2'>
                    {formatDateWithoutTime(listData?.from_date) === formatDateWithoutTime(listData?.to_date)
                      ? formatDateWithoutTime(listData?.from_date)
                      : `${formatDateWithoutTime(listData?.from_date)} - ${formatDateWithoutTime(listData?.to_date)}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="d-flex align-items-sm-center justify-content-center text-center">
                <div className="symbol symbol-150px me-5">
                  <span className="symbol-label d-flex justify-content-center align-items-center">
                    <img
                      src={toAbsoluteUrl('/media/svg/general/gift-boxes.png')}
                      className="h-50 align-self-center"
                      alt="No Holiday Found."
                    />
                  </span>
                </div>
                <span className="text-muted fw-bold fs-6 mt-2">No Holidays Found.</span>
              </div>
        )}
      </div>
    </div>
  );
};

export { HolidayWidget };
